import { HttpMethodEnum, getLocalStorage } from '@ipevo-rd/service-web-utils'
import {
	AdminApiError,
	adminHttpClient,
	handleAdminResponse,
} from './client.js'
import { getBaseAPIUrl } from '../../api.js'
import type { ApiResponse, SpeakerStats, ViewerStats } from './dto.js'

export { AdminApiError as StatsApiError } from './client.js'

/**
 * 共用 GET 請求：發送、檢查業務碼、回傳 data
 * @param url 含 query 的完整路徑
 * @returns 對應型別的資料
 */
const getJson = async <T>(url: string): Promise<T | undefined> => {
	// 透過 admin client 發送請求並由共用流程檢查業務碼
	const body = await adminHttpClient.request<T>({
		url,
		method: HttpMethodEnum.GET,
	})

	// 回傳對應型別的資料
	return body.data
}

/**
 * 組合統計 API 的查詢字串（code / from / to 三者皆必填）
 * @param code channel 短碼
 * @param from 區間起（RFC 3339 字串）
 * @param to 區間迄（RFC 3339 字串）
 * @returns 已編碼的 query string
 */
const buildStatsQuery = (code: string, from: string, to: string): string =>
	// URLSearchParams 自動 encode；三個參數固定都帶
	new URLSearchParams({ code, from, to }).toString()

/**
 * 取得主講累計在線時長（GET /v1/admin/stats/speaker）
 * @param code channel 短碼
 * @param from 區間起（RFC 3339 字串）
 * @param to 區間迄（RFC 3339 字串）
 * @returns 主講時長統計
 */
export const getSpeakerStats = (code: string, from: string, to: string) =>
	// 帶 code / from / to 查主講時長
	getJson<SpeakerStats>(
		`/admin/stats/speaker?${buildStatsQuery(code, from, to)}`,
	)

/**
 * 取得觀眾語言分佈與平均時長（GET /v1/admin/stats/viewer）
 * @param code channel 短碼
 * @param from 區間起（RFC 3339 字串）
 * @param to 區間迄（RFC 3339 字串）
 * @returns 觀眾語言統計
 */
export const getViewerStats = (code: string, from: string, to: string) =>
	// 帶 code / from / to 查觀眾語言統計
	getJson<ViewerStats>(
		`/admin/stats/viewer?${buildStatsQuery(code, from, to)}`,
	)

/**
 * 共用統計檔下載：帶 token 打指定端點，分流 JSON 錯誤與檔案 blob（CSV / xlsx 皆適用）
 * - 成功回檔案 blob；查無資料 / 參數錯時改回 JSON（一律 HTTP 200），故先看 Content-Type
 * - V1 response interceptor 會吃掉 header，這裡維持原生 fetch 自己判斷
 * @param path 端點路徑（不含 /v1 前綴與 query），如 `/admin/stats/viewer.csv`
 * @param code channel 短碼
 * @param from 區間起（RFC 3339 字串）
 * @param to 區間迄（RFC 3339 字串）
 * @returns 檔案 blob 與後端建議檔名（無 Content-Disposition 時回 undefined）
 */
const downloadStatsFile = async (
	path: string,
	code: string,
	from: string,
	to: string,
): Promise<{ blob: Blob; filename?: string }> => {
	// 從 localStorage 取 token，與既有 V1 request interceptor 取法一致
	const storedToken = getLocalStorage('token')
	// 僅在是字串時採用，避免 boolean / null 灌進 header
	const token = typeof storedToken === 'string' ? storedToken : ''

	// 帶 Authorization 打端點（不能用純 <a download> 因為要帶 token）
	const res = await fetch(
		`${getBaseAPIUrl()}/v1${path}?${buildStatsQuery(code, from, to)}`,
		{
			headers: token ? { Authorization: `Bearer ${token}` } : {},
		},
	)

	// 先看 Content-Type：是 JSON 代表出錯（查無資料 / 參數錯 / 未授權）
	const contentType = res.headers.get('content-type') ?? ''
	// JSON 分支：解析統一回應格式並以 StatsApiError 拋錯，交由呼叫端對照錯誤碼表
	if (contentType.includes('application/json')) {
		// 解析 JSON body，並轉成 ApiResponse<unknown> 型別
		const body = (await res.json()) as ApiResponse<unknown>
		// 套用一般 admin 請求相同的格式驗證、認證 callback 與業務錯誤處理
		const response = handleAdminResponse<unknown>(body, {
			requestToken: storedToken,
		})
		// 下載端點回傳 JSON 即代表未取得檔案，即使業務碼為成功仍維持錯誤語義
		throw new AdminApiError(response.code, response.message)
	}

	// 非 JSON 回應仍須確認 HTTP 成功，避免把閘道或伺服器錯誤頁當成下載檔案
	if (!res.ok) {
		// 保留 HTTP 狀態與文字，提供可追查的傳輸層錯誤
		throw new Error(
			`統計檔下載失敗：HTTP ${res.status}${
				res.statusText ? ` ${res.statusText}` : ''
			}`,
		)
	}

	// 從 Content-Disposition 取後端建議檔名（filename="..."）
	const disposition = res.headers.get('content-disposition') ?? ''
	// 比對 filename= 後的值，取不到時留 undefined 讓呼叫端用備援檔名
	const filename = /filename="?([^"]+)"?/.exec(disposition)?.[1]

	// 回傳檔案 blob 與檔名，下載動作交給呼叫端（瀏覽器 DOM 操作）
	return { blob: await res.blob(), filename }
}

/**
 * 下載觀眾 session 原始明細 CSV（GET /v1/admin/stats/viewer.csv）
 * - 即時聚合的原始觀眾列（含在線/未結算），與結算明細是不同資料源
 * @param code channel 短碼
 * @param from 區間起（RFC 3339 字串）
 * @param to 區間迄（RFC 3339 字串）
 * @returns CSV blob 與後端建議檔名（無 Content-Disposition 時回 undefined）
 */
export const downloadViewerCsv = (code: string, from: string, to: string) =>
	// 打觀眾原始明細 CSV 端點
	downloadStatsFile('/admin/stats/viewer.csv', code, from, to)

/**
 * 下載逐場結算明細多分頁 xlsx（GET /v1/admin/stats/sessions.xlsx）
 * - 資料源為 Worker settlement loop 預先算好的結算欄位，只含已結算的場（剛結束約 4 分鐘內的場不會出現）
 * - 第一頁為頻道跨場總和整合，後續每場一頁（依時間命名）
 * @param code channel 短碼
 * @param from 區間起（RFC 3339 字串）
 * @param to 區間迄（RFC 3339 字串）
 * @returns xlsx blob 與後端建議檔名（無 Content-Disposition 時回 undefined）
 */
export const downloadSessionXlsx = (code: string, from: string, to: string) =>
	// 打逐場結算明細 xlsx 端點
	downloadStatsFile('/admin/stats/sessions.xlsx', code, from, to)
